/* eslint-disable react/jsx-no-bind */
import { Outlet, useMatches } from "react-router-dom";
import { useCallback, useEffect } from "react";
import {
  StepsDisplayMode,
  BuildView,
  STEPS_DISPLAY_MODES,
} from "~/components/build/Show/lib/types/BuildView";
import FilterByChips from "~/components/build/Show/components/FilterByChips";
import FilterByDropdown from "~/components/build/Show/components/FilterByDropdown";
import FilterByMenu from "~/components/build/Show/components/FilterByMenu";
import { useMobileBreakpoint } from "~/components/build/Show/lib/useBreakpoints";
import { MobileStepsView } from "~/components/build/Show/components/MobileStepsView";
import StepsCount from "~/components/build/Show/components/StepsCount";
import { SubNavBar, SubNavBarRow, SubNavLink } from "~/components/build/Show/components/SubNavLink";
import { urlForView } from "~/components/build/Show/lib/urlForView";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { debounce } from "lodash";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { TableSearchInput } from "~/components/build/Show/components/TableView/TableSearchInput";
import { TableActionsDropdown } from "~/components/build/Show/components/TableView/TableActionsDropdown";
import DisplayModeDropdown from "~/components/build/Show/routes/list/-components/DisplayModeDropdown";
import { StepSearch } from "~/components/build/Show/routes/list/-components/StepSearch";
import Icon from "~/components/shared/Icon";
import { useHotkeys } from "react-hotkeys-hook";
import classNames from "classnames";

const displayModes = Features.BuildPageListView
  ? [
      { id: StepsDisplayMode.List, label: "List" },
      { id: StepsDisplayMode.Table, label: "Table" },
      { id: StepsDisplayMode.Canvas, label: "Canvas" },
      { id: StepsDisplayMode.Waterfall, label: "Waterfall" },
    ]
  : [
      { id: StepsDisplayMode.Canvas, label: "Canvas" },
      { id: StepsDisplayMode.Table, label: "Table" },
      { id: StepsDisplayMode.Waterfall, label: "Waterfall" },
    ];

export default function StepsLayout() {
  const { build } = useBuild();
  const params = useQueryParams();
  const isMobile = useMobileBreakpoint();
  const setStepsDisplayMode = useBuildStore((state) => state.setStepsDisplayMode);
  const tableSearchValue = useBuildStore((state) => state.tableSearchValue);
  const setTableSearchValue = useBuildStore((state) => state.setTableSearchValue);

  const updateSearch = useCallback(
    debounce((inputValue: string) => {
      // If the value is the same as the current search, do nothing
      // This prevents unnecessary state updates and re-renders.
      if (inputValue === tableSearchValue) {
        return;
      }

      setTableSearchValue(inputValue);
    }, 250),
    [tableSearchValue, setTableSearchValue],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      updateSearch(value);
    },
    [updateSearch],
  );

  const matches = useMatches();
  const currentViewId = [...matches].pop()?.id;

  // Sync the ephemeral stepsDisplayMode from the matched route so the
  // display mode dropdown reflects the current view after direct navigation.
  useEffect(() => {
    if (currentViewId && STEPS_DISPLAY_MODES.has(currentViewId as BuildView)) {
      setStepsDisplayMode(currentViewId as StepsDisplayMode);
    }
  }, [currentViewId, setStepsDisplayMode]);

  const showTableSearch =
    Features.TableViewSearchField && (currentViewId === BuildView.Table || isMobile);
  const isTableView = currentViewId === BuildView.Table;
  const isListView = currentViewId === BuildView.List;
  const isCanvasView = currentViewId === BuildView.Canvas;

  const isSearchOpen = useBuildStore((state) => state.isSearchOpen);
  const setSearchOpen = useBuildStore((state) => state.setSearchOpen);

  useHotkeys("s", () => setSearchOpen(true), { preventDefault: true, enabled: !isSearchOpen });
  useHotkeys("escape", () => setSearchOpen(false));

  return (
    <div className="flex min-w-0 flex-auto flex-col">
      <SubNavBar>
        <SubNavBarRow className="gap-2">
          <div
            className={classNames("flex flex-1 gap-2", {
              "flex-wrap": Features.BuildPageListView,
              "flex-1": !Features.BuildPageListView,
            })}
          >
            {Features.BuildPageListView && (isListView || isCanvasView) && <StepsCount />}

            {Features.BuildPageListView ? (
              <>
                <FilterByMenu />
                <FilterByChips />
              </>
            ) : (
              <>
                <div className="hidden gap-2 md:!flex">
                  {displayModes.map((mode) => (
                    <SubNavLink
                      to={urlForView(build.path, mode.id, params)}
                      key={mode.id}
                      onClick={() => setStepsDisplayMode(mode.id)}
                    >
                      {mode.label}
                    </SubNavLink>
                  ))}
                </div>

                <div className="self-center">
                  <FilterByDropdown />
                </div>
              </>
            )}
          </div>

          {showTableSearch && <TableSearchInput onChange={handleSearchChange} />}

          {isTableView && !isMobile && <TableActionsDropdown />}

          {Features.BuildPageListView && (
            <div className="ml-auto flex gap-1">
              {(isListView || isMobile) && <DisplayModeDropdown />}
              <button
                aria-checked={isSearchOpen}
                onClick={() => setSearchOpen(!isSearchOpen)}
                title="Search for step (S)"
                data-testid="toggle-search-button"
                className="hover:bg-text-600 group inline-flex h-7 w-7 items-center justify-center rounded-md bg-gray-100 font-medium transition-all hover:bg-purple-100 hover:text-purple-600 disabled:cursor-not-allowed disabled:opacity-50 aria-checked:bg-purple-100 aria-checked:text-purple-600"
              >
                <Icon icon="heroicons/16/solid/magnifying-glass" className="size-4" />
              </button>
            </div>
          )}
        </SubNavBarRow>

        {Features.BuildPageListView && isSearchOpen && (
          <div className="px-2.5 pb-2">
            <StepSearch />
          </div>
        )}
      </SubNavBar>

      {isMobile ? (
        <div className="relative flex-auto rounded-b-md bg-white">
          <MobileStepsView />
        </div>
      ) : (
        <Outlet />
      )}
    </div>
  );
}
